import { useRecordingSilenceWarning } from '@/hooks/useRecordingSilenceWarning';
import { pruneSilenceSnapshots } from '@/hooks/recordingSilenceStorage';
import { useRecordingState } from '@/contexts/RecordingStateContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { RecordingSilenceAlert } from './RecordingSilenceAlert';
import { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import RecordingStateDB, { MAX_CHUNKS_PER_SESSION } from "./RecordingStateDB.ts";
import { useUploadFileMutation } from "@/redux/api/documents.rtkq";
import { useAuthContext } from "@/hooks/useAuthContext";
import { useRealtimeTranscriptions } from "@/hooks/useRealtimeTranscriptions";
import { useRecordingWarning } from "@/hooks/useRecordingWarning";
import { useScreenWakeLock } from "@/hooks/useScreenWakeLock";
import { useRecordingControls } from "@/contexts/RecordingControlsContext";
import { toast } from "sonner";
import MainInterface from "./voiceRecorderAndUplaodComps/Main.Interface";
import RecordingInterface from "./voiceRecorderAndUplaodComps/Recording.Interface";
import AfterRecordingInterface from "./voiceRecorderAndUplaodComps/AfterRecording.Interface";
import GoogleDrivePicker from "./GoogleDrivePicker";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  startRecording as startRecordingAction,
  stopRecording as stopRecordingAction,
  setRecordingDuration,
  setDuration,
  setAudioSrc,
  setViewMode,
  setActiveRecordingId,
  setHasActiveRecording,
  setHasRestoredRecording,
  setIsAnalyserReady,
  resetRecording,
  restoreRecordingState,
} from "@/redux/slices/recording.slice";
import { Sentry, getTracePropagationFields } from "@/lib/sentry";
import { hasActiveTranscription, useStaleTick } from "./transcriptionStale";

const CHUNK_INTERVAL_SEC = 3; // Same as mediaRecorder.start(3000)
/** Product cap. IndexedDB allows 2200 chunks (~110 min); stop earlier and ask the user to send. */
const MAX_RECORDING_DURATION_SEC = 100 * 60;
const MAX_AUDIO_UPLOAD_BYTES = 300 * 1024 * 1024;
const FILE_TOO_LARGE_MESSAGE = "FILE_TOO_LARGE";
/** Pause/resume is hidden for now — flip to re-enable every pause button */
const IS_RECORDING_PAUSE_ENABLED = false;

const setAudioCurrentTime = (audio: HTMLAudioElement, targetTime: number): number => {
  const boundedTarget = Math.max(0, targetTime);
  const fastSeek = (audio as HTMLAudioElement & { fastSeek?: (time: number) => void }).fastSeek;
  if (typeof fastSeek === "function") {
    try {
      fastSeek.call(audio, boundedTarget);
      return boundedTarget;
    } catch {
      // Fallback to direct assignment if fastSeek fails in this browser/runtime.
    }
  }
  audio.currentTime = boundedTarget;
  return audio.currentTime;
};

/** WebM Cluster element ID (EBML) - start of media data. Strip init segment from continuation chunk. */
const WEBM_CLUSTER_ID = new Uint8Array([0x1f, 0x43, 0xb6, 0x75]);

/** Result of stripping WebM init: blob to use, and whether init was found (so we can skip init-only chunks). */
const stripWebMInitSegment = async (blob: Blob): Promise<{ blob: Blob; hasCluster: boolean }> => {
  const buf = await blob.arrayBuffer();
  const arr = new Uint8Array(buf);
  for (let i = 0; i <= arr.length - WEBM_CLUSTER_ID.length; i++) {
    if (
      arr[i] === WEBM_CLUSTER_ID[0] &&
      arr[i + 1] === WEBM_CLUSTER_ID[1] &&
      arr[i + 2] === WEBM_CLUSTER_ID[2] &&
      arr[i + 3] === WEBM_CLUSTER_ID[3]
    ) {
      const stripped = new Blob([buf.slice(i)], { type: blob.type });
      return { blob: stripped, hasCluster: true };
    }
  }
  return { blob, hasCluster: false };
};

/**
 * WebKit sometimes fails with blob URLs for short clips, but very large `data:` URLs
 * can fail to load on long recordings. Use data URL only for relatively small blobs.
 */
const isWebKit =
  typeof navigator !== "undefined" &&
  /AppleWebKit|Safari/.test(navigator.userAgent) &&
  !/Chrome|Chromium/.test(navigator.userAgent);
const MAX_DATA_URL_AUDIO_BYTES = 20 * 1024 * 1024;

const blobToAudioSrc = (blob: Blob): Promise<string> => {
  if (isWebKit && blob.size <= MAX_DATA_URL_AUDIO_BYTES) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }
  return Promise.resolve(URL.createObjectURL(blob));
};

/** Must match MediaRecorder.start(timeslice) below */
const CHUNK_TIMESLICE_MS = 3000;
const WEBM_EBML_HEADER = new Uint8Array([0x1a, 0x45, 0xdf, 0xa3]);
/** ~4kbps floor — real Opus speech is ~64kbps; catches near-empty / headerless blobs */
const MIN_BYTES_PER_SECOND = 500;

const hasWebmEbmlHeader = async (blob: Blob): Promise<boolean> => {
  if (blob.size < WEBM_EBML_HEADER.length) return false;
  const header = new Uint8Array(await blob.slice(0, WEBM_EBML_HEADER.length).arrayBuffer());
  return WEBM_EBML_HEADER.every((byte, index) => header[index] === byte);
};

const isAudioBlobPlausibleForDuration = (blob: Blob, durationSeconds: number): boolean => {
  if (blob.size <= 0) return false;
  // Chrome webm often reports Infinity/NaN; don't treat a real file as "too small".
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return blob.size > 100;
  if (durationSeconds < 3) return blob.size > 100;
  return blob.size >= durationSeconds * MIN_BYTES_PER_SECOND;
};

const validateRecordingBlobForUpload = async (
  blob: Blob,
  mimeType: string,
  durationSeconds: number
): Promise<string | null> => {
  if (blob.size > MAX_AUDIO_UPLOAD_BYTES) {
    return FILE_TOO_LARGE_MESSAGE;
  }
  const isWebm = mimeType.includes("webm") || mimeType.includes("opus");
  if (isWebm && !(await hasWebmEbmlHeader(blob))) {
    return "קובץ ההקלטה פגום (חסר כותרת WebM). נסה להקליט מחדש.";
  }
  if (!isAudioBlobPlausibleForDuration(blob, durationSeconds)) {
    return "קובץ ההקלטה קטן מדי ביחס למשך ההקלטה. נסה להקליט מחדש.";
  }
  return null;
};

interface VoiceRecorderAndUploadProps {
  pendingTranscription: {
    fileName: string;
    currentStep: "uploading" | "queued" | "processing" | "completed" | "error";
    progress: number;
    error?: string;
    id?: string;
    createdAt?: string;
    storagePath?: string;
  } | null;
  setPendingTranscription: (
    transcription: {
      fileName: string;
      currentStep: "uploading" | "queued" | "processing" | "completed" | "error";
      progress: number;
      error?: string;
      id?: string;
      createdAt?: string;
      storagePath?: string;
    } | null
  ) => void;
  isReadOnly?: boolean;
  isArchiveMode?: boolean;
}

export default function VoiceRecorderAndUpload({
  pendingTranscription,
  setPendingTranscription,
  isReadOnly = false,
  isArchiveMode = false,
}: VoiceRecorderAndUploadProps) {
  const dispatch = useDispatch();
  const { stopRecordingRef, continueRecordingRef, pauseRecordingRef, dismissSilenceWarningRef } = useRecordingControls();
  const { isRecordingPaused, setIsRecordingPaused, setIsSilenceWarningVisible } = useRecordingState();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Redux state
  const {
    viewMode,
    audioSrc,
    isRecording,
    duration,
    recordingDuration,
    activeRecordingId,
    hasActiveRecording,
    hasRestoredRecording,
    isAnalyserReady,
  } = useSelector(
    (state: {
      recording: {
        viewMode: "main" | "recording" | "after-record";
        audioSrc: string | null;
        isRecording: boolean;
        duration: number;
        recordingDuration: number;
        activeRecordingId: string | null;
        hasActiveRecording: boolean;
        hasRestoredRecording: boolean;
        isAnalyserReady: boolean;
      };
    }) => state.recording
  );

  const silenceMonitorRef = useRef<Pick<ReturnType<typeof useRecordingSilenceWarning>, 'reset' | 'finish'> | null>(null);

  // Local state for UI only
  const [currentTime, setCurrentTime] = useState(0);
  const [localFileName, setLocalFileName] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showMaxDurationDialog, setShowMaxDurationDialog] = useState(false);
  const [showFileTooLargeDialog, setShowFileTooLargeDialog] = useState(false);
  const [showGoogleDrivePicker, setShowGoogleDrivePicker] = useState(false);

  // Browser warning hook
  useRecordingWarning(isRecording);
  // Mobile auto-lock suspends the mic — keep the screen on while recording
  useScreenWakeLock(isRecording);

  // Custom hooks
  const { user, getAccountId } = useAuthContext() as {
    user: NonNullable<ReturnType<typeof useAuthContext>["user"]>;
    getAccountId: () => string | undefined;
  };
  const silenceStorageScope = user?.uid ? JSON.stringify(['suite', getAccountId() ?? '', user.uid]) : null;
  const [uploadFile, { isSuccess: isUploadSuccess, isError: isUploadError, error: uploadError }] =
    useUploadFileMutation();
  const [isSubmittingUpload, setIsSubmittingUpload] = useState(false);

  // Real-time transcriptions hook to track upload progress
  const { transcriptions } = useRealtimeTranscriptions({
    userId: user?.uid || "",
    enabled: !!user?.uid,
  });

  // A stuck recording never updates its document, so re-check on a timer.
  const staleTick = useStaleTick(hasActiveTranscription(transcriptions));

  // Track uploaded file progress in real-time — only the file we just sent
  useEffect(() => {
    if (!transcriptions.length || !uploadedFileNameRef.current) return;

    const trackedId = uploadedTranscriptionIdRef.current;
    const trackedName = uploadedFileNameRef.current;

    let uploadedFile = trackedId
      ? transcriptions.find((t) => t.id === trackedId)
      : undefined;

    if (!uploadedFile) {
      uploadedFile = transcriptions.find(
        (t) => t.fileName === trackedName || t.fileName.startsWith(`${trackedName}.`)
      );
    }

    if (!uploadedFile) {
      uploadedFile = transcriptions.find(
        (t) => t.fileName.includes(trackedName) || t.storagePath.includes(trackedName)
      );
    }

    if (!uploadedFile) {
      const recentAudioFiles = transcriptions
        .filter((t) => !t.isDeleted && (t.fileName.includes("audio_recording_") || t.fileName.includes("audio_rec_")))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      if (recentAudioFiles.length > 0) {
        uploadedFile = recentAudioFiles[0];
      }
    }

    if (uploadedFile) {
      if (uploadedFile.isDeleted) {
        uploadedFileNameRef.current = null;
        uploadedTranscriptionIdRef.current = null;
        lastProcessedStatusRef.current = null;
        setPendingTranscription(null);
        return;
      }

      const status = uploadedFile.processing.status;
      const statusKey = `${uploadedFile.id}_${status}`;

      // Prevent infinite loop - only update if status actually changed
      if (lastProcessedStatusRef.current === statusKey) {
        return;
      }

      lastProcessedStatusRef.current = statusKey;

      Sentry.addBreadcrumb({
        category: "transcription.pipeline",
        message: `Transcription status: ${status}`,
        level: status === "failed" ? "error" : "info",
        data: {
          transcriptionId: uploadedFile.id,
          fileName: uploadedFile.fileName,
          storagePath: uploadedFile.storagePath,
          status,
          errorCode: uploadedFile.processing.errorCode,
          error: uploadedFile.processing.error,
        },
      });
      Sentry.setTag("transcription_id", uploadedFile.id);

      let currentStep: "uploading" | "queued" | "processing" | "completed" | "error" = "uploading";
      let progress = 0;

      switch (status) {
        case "processing":
          currentStep = "processing";
          progress = 75;
          break;
        case "completed":
          currentStep = "completed";
          progress = 100;
          // Don't clear pending transcription immediately - let it stay visible
          // until the actual transcription appears in the table
          toast.success("התמלול הושלם בהצלחה!");
          break;
        case "failed": {
          const errorMessage = uploadedFile.processing.error || "שגיאה בעיבוד ההקלטה";
          toast.error(errorMessage);
          uploadedFileNameRef.current = null;
          uploadedTranscriptionIdRef.current = null;
          setIsSubmittingUpload(false);
          setPendingTranscription({
            fileName: uploadedFile.fileName,
            currentStep: "error",
            progress: 0,
            error: errorMessage,
            id: uploadedFile.id,
            createdAt: uploadedFile.createdAt,
            storagePath: uploadedFile.storagePath,
          });
          return;
        }
        default:
          currentStep = "queued";
          progress = 50;
      }

      // Update pending transcription with new status
      setPendingTranscription({
        fileName: uploadedFile.fileName,
        currentStep,
        progress,
        id: uploadedFile.id,
        createdAt: uploadedFile.createdAt,
        storagePath: uploadedFile.storagePath,
      });
    } else {
      console.error("❌ Uploaded file not found in transcriptions");
    }
  }, [transcriptions, setPendingTranscription, staleTick]);

  // Refs for audio handling
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const chunkCounterRef = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const uploadedFileNameRef = useRef<string | null>(null);
  const uploadedTranscriptionIdRef = useRef<string | null>(null);
  const durationCounterRef = useRef(0); // Track duration to avoid closure issues
  const pauseStartRef = useRef<number | null>(null);
  const pausedDurationRef = useRef(0);
  const recordingStartTimeRef = useRef<number>(0); // Track actual start time
  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;
  const getElapsedSeconds = useCallback(() => Math.max(0,
    ((pauseStartRef.current ?? Date.now()) - recordingStartTimeRef.current - pausedDurationRef.current) / 1000), []);
  const hasShownUploadSuccessRef = useRef(false); // Prevent double toasts
  const lastProcessedStatusRef = useRef<string | null>(null); // Track last processed status to prevent infinite loop
  const startInFlightRef = useRef(false);
  const maxDurationStopRequestedRef = useRef(false);
  const requestMaxDurationStopRef = useRef<() => void>(() => {});
  /** Set when the OS interrupted the mic (e.g. phone locked); recording resumes in a new session. */
  const shouldAutoContinueRef = useRef(false);

  /** When set, playback is in two parts (continuation); part2 plays after part1 ends. */
  const continuationPart1DurationRef = useRef(0);
  const continuationPart2SrcRef = useRef<string | null>(null);
  const continuationPart1SrcRef = useRef<string | null>(null); // Stored so we can seek back into part1
  const nowPlayingPart2Ref = useRef(false);
  /** Full first chunk of new session (with init) for part2 playback; part2 needs init to decode. */
  const continuationFirstNewChunkFullRef = useRef<Blob | null>(null);
  const sourceSwitchRequestRef = useRef(0);
  const pendingCanPlayHandlerRef = useRef<((event: Event) => void) | null>(null);

  const clearContinuationRefs = useCallback(() => {
    if (continuationPart2SrcRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(continuationPart2SrcRef.current);
    }
    continuationPart1DurationRef.current = 0;
    continuationPart2SrcRef.current = null;
    continuationPart1SrcRef.current = null;
    continuationFirstNewChunkFullRef.current = null;
    nowPlayingPart2Ref.current = false;
  }, []);

  const checkForActiveRecordings = useCallback(async () => {
    try {
      const restorable = await RecordingStateDB.getRestorableRecordings();
      if (silenceStorageScope) {
        void pruneSilenceSnapshots(silenceStorageScope, restorable.map((recording) => recording.id)).catch(() => {});
      }
      if (restorable.length === 0) return;

      // Prefer recording/ready_to_upload; among equals pick most recently updated
      const mostRecent = restorable.reduce((latest, cur) => {
        const rank = (r: (typeof restorable)[number]) =>
          r.phase === "recording" ? 2 : r.phase === "ready_to_upload" || r.isActive ? 1 : 0;
        const rankDiff = rank(cur) - rank(latest);
        if (rankDiff !== 0) return rankDiff > 0 ? cur : latest;
        return cur.lastUpdated > latest.lastUpdated ? cur : latest;
      });

      // Skip empty shells (state without chunks — common after IDB corruption / partial delete)
      const chunkCount = await RecordingStateDB.countRecordingChunks(mostRecent.id);
      if (chunkCount === 0) {
        console.warn("Skipping empty recording state:", mostRecent.id);
        return;
      }

      dispatch(setHasActiveRecording(true));
      dispatch(setActiveRecordingId(mostRecent.id));
    } catch (error) {
      console.error("Error checking for active recordings:", error);
    }
  }, [dispatch, silenceStorageScope]);

  const updateCurrentAudioFile = useCallback(
    (file: File) => {
      // External file — never two-part playback
      clearContinuationRefs();
      const fileURL = URL.createObjectURL(file);
      dispatch(setAudioSrc(fileURL));
      setLocalFileName(file.name);
      dispatch(setActiveRecordingId(null));
      dispatch(setHasActiveRecording(false));
      // Reset duration to 0 initially, it will be set when metadata loads
      dispatch(setDuration(0));
    },
    [dispatch, clearContinuationRefs]
  );

  const processFiles = useCallback(
    (filesList: FileList | null): boolean => {
      if (filesList && filesList.length > 0) {
        const file = filesList[0];
        if (file.size <= 0) {
          const errorMessage = "קובץ השמע ריק. בדוק את המיקרופון ונסה להקליט או להעלות שוב.";
          toast.error(errorMessage);
          setPendingTranscription({
            fileName: file.name,
            currentStep: "error",
            progress: 0,
            error: errorMessage,
          });
          return false;
        }
        if (file.size > MAX_AUDIO_UPLOAD_BYTES) {
          setShowFileTooLargeDialog(true);
          return false;
        }
        updateCurrentAudioFile(file);
        return true;
      }
      return false;
    },
    [setPendingTranscription, updateCurrentAudioFile]
  );

  // Handle file drop from global drag detection
  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (isReadOnly) {
        toast.error("לא ניתן להעלות קבצים - תקופת הניסיון הסתיימה. שדרג למינוי בתשלום.");
        navigate("/");
        return;
      }
      if (
        isSubmittingUpload ||
        (pendingTranscription &&
          pendingTranscription.currentStep !== "error" &&
          pendingTranscription.currentStep !== "completed")
      ) {
        toast.error("העלאה בתהליך — נא להמתין לסיום");
        return;
      }
      if (acceptedFiles.length === 0) {
        toast.error(`יש להעלות קבצי אודיו או וידאו בלבד`);
        return;
      }

      // Create a new FileList-like object with accepted files
      const dataTransfer = new DataTransfer();
      acceptedFiles.forEach((file) => dataTransfer.items.add(file));

      if (processFiles(dataTransfer.files)) {
        dispatch(setViewMode("after-record"));
      }
    },
    [processFiles, dispatch, isReadOnly, navigate, isSubmittingUpload, pendingTranscription]
  );

  // Handle upload success/error states
  useEffect(() => {
    if (isUploadSuccess && !hasShownUploadSuccessRef.current) {
      hasShownUploadSuccessRef.current = true;
      toast.success("הקובץ הועלה בהצלחה!");
    }
  }, [isUploadSuccess]);

  // Reset upload success flag when starting new recording
  useEffect(() => {
    if (isRecording) {
      hasShownUploadSuccessRef.current = false;
    }
  }, [isRecording]);

  useEffect(() => {
    if (isUploadError && uploadError) {
      const errorMessage =
        "data" in uploadError &&
          uploadError.data &&
          typeof uploadError.data === "object" &&
          "error" in uploadError.data &&
          uploadError.data.error &&
          typeof uploadError.data.error === "object" &&
          "message" in uploadError.data.error
          ? uploadError.data.error.message
          : "שגיאה בהעלאת הקובץ";
      toast.error(`שגיאה בהעלאת הקובץ: ${errorMessage}`);
    }
  }, [isUploadError, uploadError]);

  // Initialize IndexedDB and handle active recordings
  useEffect(() => {
    const initDB = async () => {
      try {
        await RecordingStateDB.init();
        try {
          // Purge uploaded leftovers / keep at most one restorable session
          await RecordingStateDB.enforceCrashRecoveryPolicy();
        } catch (policyError) {
          console.warn("Crash-recovery policy failed (non-fatal):", policyError);
        }
        // Check for any active recordings on startup
        await checkForActiveRecordings();
      } catch (error) {
        console.error("Failed to initialize recording database:", error);
        toast.error("Failed to initialize recording storage");
      }
    };

    initDB();
  }, [checkForActiveRecordings]);

  // Only clean up mic/AudioContext on real unmount, not when isRecording flips to true
  useEffect(() => {
    return () => {
      if (isRecordingRef.current) return;
      audioContextRef.current?.close();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  // UI timer tied to recording lifecycle
  useEffect(() => {
    if (!isRecording || isRecordingPaused) return;

    const tick = () => {
      if (!recordingStartTimeRef.current) return;
      const elapsedSeconds = getElapsedSeconds();
      durationCounterRef.current = elapsedSeconds;
      dispatch(setRecordingDuration(elapsedSeconds));
      dispatch(setDuration(elapsedSeconds));
    };

    tick();
    const id = window.setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isRecording, isRecordingPaused, dispatch, getElapsedSeconds]);

  useEffect(() => {
    // Add event listeners to the document
    const handleDocumentDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    document.addEventListener("dragover", handleDocumentDragOver);
    return () => {
      document.removeEventListener("dragover", handleDocumentDragOver);
    };
  }, []);

  // Waveform visualization functions
  const drawWaveform = useCallback((dataArray: Uint8Array) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    if (!canvasCtx) return;

    const WIDTH = canvas.width;
    const HEIGHT = canvas.height;

    canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
    canvasCtx.fillStyle = "#4f46e5"; // chen-600 color

    const barWidth = 2;
    const spacing = 4;
    const maxBarHeight = HEIGHT / 2.5;
    const numBars = Math.floor(WIDTH / (barWidth + spacing));

    for (let i = 0; i < numBars; i++) {
      const barHeight = Math.pow(dataArray[i] / 128.0, 2) * maxBarHeight;
      const x = (barWidth + spacing) * i;
      const y = HEIGHT / 2 - barHeight / 2;
      canvasCtx.fillRect(x, y, barWidth, barHeight);
    }
  }, []);

  const drawStaticPattern = useCallback(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    if (!canvasCtx) return;

    const WIDTH = canvas.width;
    const HEIGHT = canvas.height;

    canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
    canvasCtx.fillStyle = "#4f46e5";

    const barWidth = 2;
    const spacing = 4;
    const maxBarHeight = HEIGHT / 2.5;
    const numBars = Math.floor(WIDTH / (barWidth + spacing));

    // Draw a static pattern with random heights
    for (let i = 0; i < numBars; i++) {
      const barHeight = Math.sin(i * 0.3) * maxBarHeight * 0.3 + maxBarHeight * 0.2;
      const x = (barWidth + spacing) * i;
      const y = HEIGHT / 2 - barHeight / 2;
      canvasCtx.fillRect(x, y, barWidth, barHeight);
    }
  }, []);

  const visualizeVolume = useCallback(() => {
    if (!analyserRef.current || !isRecording || !canvasRef.current) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!isRecording || !analyserRef.current || !canvasRef.current) {
        cancelAnimationFrame(animationFrameRef.current || 0);
        return;
      }
      animationFrameRef.current = requestAnimationFrame(draw);
      analyserRef.current.getByteTimeDomainData(dataArray);
      drawWaveform(dataArray);
    };

    draw();
  }, [isRecording, drawWaveform]);

  // Effect to start/stop waveform visualization
  useEffect(() => {
    if (isRecording && canvasRef.current) {
      if (isAnalyserReady) {
        // Start real-time visualization when analyser is ready
        visualizeVolume();
      } else {
        // Show static pattern while analyser is being set up
        drawStaticPattern();
      }

      return () => {
        cancelAnimationFrame(animationFrameRef.current || 0);
      };
    } else {
      cancelAnimationFrame(animationFrameRef.current || 0);
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const canvasCtx = canvas.getContext("2d");
        if (canvasCtx) {
          canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
    }
  }, [isRecording, isAnalyserReady, visualizeVolume, drawStaticPattern]);

  // Function to restore audio from active recording without starting new recording
  const restoreAudioFromActiveRecording = useCallback(async () => {
    if (!activeRecordingId) return;

    try {
      const recordingState = await RecordingStateDB.getRecordingState(activeRecordingId);
      const chunks = await RecordingStateDB.getRecordingChunks(activeRecordingId);

      if (recordingState && chunks.length > 0) {
        // Wall-clock duration: chunks are produced every CHUNK_INTERVAL_SEC (timeslice 3000ms)
        const totalDuration = chunks.length * CHUNK_INTERVAL_SEC;
        const mimeType = recordingState.mimeType || "audio/webm;codecs=opus";

        // New chunks store ArrayBuffer; legacy chunks store Blob in .data
        const arrays: ArrayBuffer[] = [];
        for (const chunk of chunks) {
          if (chunk.buffer) {
            arrays.push(chunk.buffer);
          } else if (chunk.data) {
            try {
              arrays.push(await chunk.data.arrayBuffer());
            } catch {
              // Skip invalid blob (e.g. Safari IDB blob)
            }
          }
        }
        if (arrays.length === 0) {
          console.warn("Restore: no valid chunks", { total: chunks.length });
          toast.error("שגיאה בשחזור ההקלטה");
          return;
        }

        // Detect session boundary: chunk (other than first) starting with EBML header = new MediaRecorder session
        const EBML_HEADER = [0x1a, 0x45, 0xdf, 0xa3];
        let sessionBoundary = -1;
        for (let i = 1; i < arrays.length; i++) {
          const view = new Uint8Array(arrays[i]);
          if (
            view.length >= 4 &&
            view[0] === EBML_HEADER[0] &&
            view[1] === EBML_HEADER[1] &&
            view[2] === EBML_HEADER[2] &&
            view[3] === EBML_HEADER[3]
          ) {
            sessionBoundary = i;
            break;
          }
        }

        chunkCounterRef.current = chunks.length;
        // Keep individual blobs per chunk so continue recording can work
        audioChunksRef.current = arrays.map((ab) => new Blob([ab], { type: mimeType }));

        let audioURL: string;
        if (sessionBoundary > 0) {
          // Multi-session: two-part playback (merged WebM is not playable across boundary)
          const part1Blob = new Blob(arrays.slice(0, sessionBoundary), { type: mimeType });
          const part2Blob = new Blob(arrays.slice(sessionBoundary), { type: mimeType });
          const [part1URL, part2URL] = await Promise.all([
            blobToAudioSrc(part1Blob),
            blobToAudioSrc(part2Blob),
          ]);
          continuationPart1DurationRef.current = sessionBoundary * CHUNK_INTERVAL_SEC;
          continuationPart2SrcRef.current = part2URL;
          continuationPart1SrcRef.current = part1URL;
          continuationFirstNewChunkFullRef.current = null;
          nowPlayingPart2Ref.current = false;
          audioURL = part1URL;
        } else {
          const blob = new Blob(arrays, { type: mimeType });
          audioURL = await blobToAudioSrc(blob);
          clearContinuationRefs();
        }

        // Dispatch restore action to Redux
        dispatch(
          restoreRecordingState({
            recordingId: activeRecordingId,
            duration: totalDuration,
            audioUrl: audioURL,
          })
        );

        toast.info(`הקלטה פעילה נמצאה. לחיצה על "המשך הקלטה" תמשיך להקליט מנקודת העצירה.`, { duration: 6000 });
      }
    } catch (error) {
      console.error("Error restoring audio from active recording:", error);
      toast.error("שגיאה בשחזור ההקלטה");
    }
  }, [activeRecordingId, dispatch, clearContinuationRefs]);

  // Handle active recording restoration
  useEffect(() => {
    if (hasActiveRecording && activeRecordingId && !hasRestoredRecording) {
      // If there's an active recording, automatically restore it and show the audio player
      restoreAudioFromActiveRecording();
    }
  }, [hasActiveRecording, activeRecordingId, hasRestoredRecording, restoreAudioFromActiveRecording]);

  const discardActiveRecording = useCallback(async () => {
    if (!activeRecordingId || !RecordingStateDB) return;
    silenceMonitorRef.current?.finish();

    try {
      await RecordingStateDB.deleteRecordingById(activeRecordingId);
      dispatch(setHasActiveRecording(false));
      dispatch(setActiveRecordingId(null));
      dispatch(setHasRestoredRecording(false));
      toast.success("הקלטה נמחקה בהצלחה");
    } catch (error) {
      console.error("Error discarding recording:", error);
      toast.error("שגיאה במחיקת ההקלטה");
    }
  }, [activeRecordingId, dispatch]);

  const uploadAudio = useCallback(async () => {
    if (isSubmittingUpload) {
      return;
    }

    try {
      if (!audioSrc) {
        console.error("[uploadAudio] No audioSrc provided");
        toast.error("אין קובץ אודיו להעלאה");
        return;
      }

      setIsSubmittingUpload(true);

      // Reset upload success flag for new upload
      hasShownUploadSuccessRef.current = false;
      lastProcessedStatusRef.current = null; // Reset status tracking

      // Convert the audio source to a File object

      let blob: Blob;

      // Try to use chunks directly if available (more reliable for large files)
      if (audioChunksRef.current.length > 0) {
        // Get mime type from the first chunk or fetch headers
        let mimeType = audioChunksRef.current[0]?.type || "audio/webm;codecs=opus";

        // Try to get more accurate mime type from blob URL headers (HEAD request would be ideal but not always supported)
        // For blob URLs, we can check the response headers without reading the body
        if (!mimeType || mimeType === "application/octet-stream") {
          try {
            const headResponse = await fetch(audioSrc, { method: "HEAD" });
            const contentType = headResponse.headers.get("content-type");
            if (contentType) {
              mimeType = contentType;
            }
          } catch (e) {
            // If HEAD fails, try a regular fetch but cancel immediately
            try {
              const response = await fetch(audioSrc);
              const contentType = response.headers.get("content-type");
              if (contentType) {
                mimeType = contentType;
              }
              response.body?.cancel(); // Cancel to avoid reading large body
            } catch (e2) {
              console.warn("[uploadAudio] Could not get mime type from response, using inferred:", mimeType);
            }
          }
        }

        blob = new Blob(audioChunksRef.current, { type: mimeType });
      } else {
        // Fallback to fetching if chunks not available
        let response: Response;
        try {
          response = await fetch(audioSrc);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        } catch (fetchError) {
          console.error("[uploadAudio] Fetch error details:", fetchError);
          console.error("[uploadAudio] Fetch error name:", fetchError instanceof Error ? fetchError.name : "Unknown");
          console.error("[uploadAudio] Fetch error message:", fetchError instanceof Error ? fetchError.message : "Unknown");
          console.error("[uploadAudio] Fetch error stack:", fetchError instanceof Error ? fetchError.stack : "No stack");
          throw fetchError;
        }

        // Use arrayBuffer instead of blob() for large files - more reliable
        try {
          const arrayBuffer = await response.arrayBuffer();

          const contentType = response.headers.get("content-type") || "audio/webm";
          blob = new Blob([arrayBuffer], { type: contentType });
        } catch (blobError) {
          console.error("[uploadAudio] Blob creation error:", blobError);
          console.error("[uploadAudio] Blob error name:", blobError instanceof Error ? blobError.name : "Unknown");
          console.error("[uploadAudio] Blob error message:", blobError instanceof Error ? blobError.message : "Unknown");
          console.error("[uploadAudio] Blob error stack:", blobError instanceof Error ? blobError.stack : "No stack");
          throw blobError;
        }
      }

      // Create a file name with timestamp
      const dateNow = new Date().toLocaleDateString("he-IL", { timeZone: "Asia/Jerusalem" }).replace(/\D/g, "_");
      const timeNow = new Date().toLocaleTimeString("he-IL", { timeZone: "Asia/Jerusalem" });
      const fileName = `audio_rec_${dateNow}-${timeNow}`;

      // Detect file extension from blob type
      // IMPORTANT: Match the extension to the actual format, not a default
      let fileExtension = ".webm"; // Default to webm since that's what browsers actually support
      let mimeType = blob.type || "audio/webm";

      console.log("[uploadAudio] Blob type:", blob.type);

      if (blob.type.includes("webm") || blob.type.includes("opus")) {
        fileExtension = ".webm";
        mimeType = blob.type || "audio/webm;codecs=opus";
      } else if (blob.type.includes("m4a") || blob.type.includes("mp4")) {
        fileExtension = ".m4a";
        mimeType = blob.type || "audio/mp4";
      } else if (blob.type.includes("mp3") || blob.type.includes("mpeg")) {
        fileExtension = ".mp3";
        mimeType = blob.type || "audio/mpeg";
      } else if (blob.type.includes("wav")) {
        fileExtension = ".wav";
        mimeType = blob.type || "audio/wav";
      } else if (blob.type.includes("ogg")) {
        fileExtension = ".ogg";
        mimeType = blob.type || "audio/ogg";
      }

      console.log("[uploadAudio] Detected extension:", fileExtension);
      console.log("[uploadAudio] Using mime type:", mimeType);

      // Upload the file
      const audioDuration = recordingDuration === 0 ? audioRef?.current?.duration || 0 : recordingDuration;

      const validationError = await validateRecordingBlobForUpload(blob, mimeType, audioDuration);
      if (validationError) {
        console.error("[uploadAudio] Blob validation failed:", {
          size: blob.size,
          mimeType,
          audioDuration,
          validationError,
        });
        if (validationError === FILE_TOO_LARGE_MESSAGE) {
          setShowFileTooLargeDialog(true);
          return;
        }
        toast.error(validationError);
        setPendingTranscription({
          fileName: uploadedFileNameRef.current || fileName,
          currentStep: "error",
          progress: 0,
          error: validationError,
        });
        return;
      }

      const file = new File([blob], `${fileName}${fileExtension}`, { type: mimeType });

      // Determine the file path
      const filePath = `audio/${fileName}${fileExtension}`;

      // Store the fileName for tracking
      uploadedFileNameRef.current = fileName;

      // Show pending transcription row
      setPendingTranscription({
        fileName,
        currentStep: "uploading",
        progress: 0,
        error: undefined,
      });

      const transcriptionId = crypto.randomUUID();
      uploadedTranscriptionIdRef.current = transcriptionId;

      let result;
      try {
        result = await Sentry.startSpan(
          {
            name: "recording.upload_pipeline",
            op: "ui.action",
            attributes: {
              transcription_id: transcriptionId,
              storage_path: `${user.uid}/${filePath}`,
              user_id: user.uid,
              file_name: `${fileName}${fileExtension}`,
            },
          },
          async () => {
            Sentry.setTag("transcription_id", transcriptionId);
            Sentry.setTag("pipeline", "recording_upload");
            Sentry.setTag("storage_path", `${user.uid}/${filePath}`);
            Sentry.setTag("user_id", user.uid);

            const { sentryTrace, sentryBaggage } = getTracePropagationFields();
            const officeId = getAccountId();

            return uploadFile({
              file,
              path: filePath,
              userId: user.uid,
              metadata: {
                customMetadata: {
                  uid: user.uid,
                  state: "processing",
                  duration: audioDuration.toFixed(3),
                  transcriptionId,
                  ...(officeId ? { officeId } : {}),
                  ...(sentryTrace ? { sentryTrace } : {}),
                  ...(sentryBaggage ? { sentryBaggage } : {}),
                },
              },
            });
          }
        );
      } catch (uploadError) {
        console.error("[uploadAudio] Upload error caught:", uploadError);
        console.error("[uploadAudio] Upload error name:", uploadError instanceof Error ? uploadError.name : "Unknown");
        console.error("[uploadAudio] Upload error message:", uploadError instanceof Error ? uploadError.message : "Unknown");
        console.error("[uploadAudio] Upload error stack:", uploadError instanceof Error ? uploadError.stack : "No stack");
        throw uploadError;
      }

      if (result.error) {
        console.error("[uploadAudio] Upload result contains error:", result.error);
        setPendingTranscription({
          fileName,
          error: "שגיאה בהעלאת הקובץ",
          currentStep: "error",
          progress: 0,
        });
        return;
      }

      setPendingTranscription({
        fileName,
        currentStep: "queued",
        progress: 25,
        error: undefined,
        id: transcriptionId,
      });

      silenceMonitorRef.current?.finish();
      // Clean up active recording after successful upload
      if (activeRecordingId && RecordingStateDB) {
        try {
          await RecordingStateDB.deleteRecordingById(activeRecordingId);
          console.log("Active recording deleted after successful upload:", activeRecordingId);
        } catch (error) {
          console.error("Error deleting active recording after upload:", error);
        }
      }

      // Reset Redux state to show main screen ready for next recording
      dispatch(resetRecording());
      setCurrentTime(0);
      setLocalFileName(null);
      clearContinuationRefs();

      // Clear audio chunks
      audioChunksRef.current = [];
      chunkCounterRef.current = 0;

      // Stop and cleanup media stream if it exists
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Clear media recorder reference
      mediaRecorderRef.current = null;
    } catch (error) {
      console.error("[uploadAudio] === ERROR IN UPLOAD ===");
      console.error("[uploadAudio] Error object:", error);
      console.error("[uploadAudio] Error type:", error instanceof Error ? error.constructor.name : typeof error);
      console.error("[uploadAudio] Error name:", error instanceof Error ? error.name : "N/A");
      console.error("[uploadAudio] Error message:", error instanceof Error ? error.message : String(error));
      console.error("[uploadAudio] Error stack:", error instanceof Error ? error.stack : "No stack trace");

      // Check if it's a fetch error
      if (error instanceof TypeError && error.message.includes("fetch")) {
        console.error("[uploadAudio] This is a fetch error - possible CORS issue or invalid URL");
        console.error("[uploadAudio] audioSrc that failed:", audioSrc);
      }

      toast.error("שגיאה בהעלאת הקובץ");
      setPendingTranscription({
        fileName: uploadedFileNameRef.current || "recording",
        error: "שגיאה בהעלאת הקובץ",
        currentStep: "error",
        progress: 0,
      });
    } finally {
      setIsSubmittingUpload(false);
    }
  }, [
    user?.uid,
    audioSrc,
    activeRecordingId,
    uploadFile,
    dispatch,
    setPendingTranscription,
    recordingDuration,
    isSubmittingUpload,
    clearContinuationRefs,
  ]);

  const handleGoogleDriveUpload = useCallback(() => {
    setShowGoogleDrivePicker(true);
  }, []);

  const handleGoogleDriveFileSelect = useCallback(
    (file: File) => {
      updateCurrentAudioFile(file);
      dispatch(setViewMode("after-record"));
      setShowGoogleDrivePicker(false);
    },
    [updateCurrentAudioFile, dispatch]
  );

  const handleGoogleDrivePickerClose = useCallback(() => {
    setShowGoogleDrivePicker(false);
  }, []);

  // Actual recording function (extracted from the original startRecording)
  const startActualRecording = useCallback(
    async (isContinuation = false) => {
      const timestamp = Date.now();
      const recordingId = `recording_${timestamp}`;
      const startTime = timestamp;

      chunkCounterRef.current = 0;
      maxDurationStopRequestedRef.current = false;
      durationCounterRef.current = 0;
      pausedDurationRef.current = 0;
      pauseStartRef.current = null;
      setIsRecordingPaused(false);
      recordingStartTimeRef.current = Date.now();
      setLocalFileName(null);

      dispatch(startRecordingAction({ recordingId }));

      try {
        // Run non-blocking operations in parallel
        const [stream] = await Promise.all([
          navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              // Note: sampleRate constraint is ignored by browsers - they use hardware rate
              // Backend will resample to 16kHz for Whisper
              channelCount: 1, // Mono audio for speech
            },
          }),
          // Single-session invariant: drop all other sessions when starting fresh
          !isContinuation ? RecordingStateDB.beginExclusiveSession(recordingId) : Promise.resolve(),
        ]);

        streamRef.current = stream;

        // Set up audio analyzer for visualization (non-blocking)
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        analyserRef.current = analyser;
        audioContextRef.current = audioContext;

        // Mark analyser as ready for visualization
        dispatch(setIsAnalyserReady(true));

        // Determine best supported audio format
        // Note: WAV is NOT supported by MediaRecorder in most browsers
        // WebM/Opus is the standard format and works best for speech
        let mimeType = "audio/webm;codecs=opus";
        const options: MediaRecorderOptions = {
          audioBitsPerSecond: 64000, // 64kbps - good quality for speech, ~28MB per hour
        };

        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
          mimeType = "audio/webm;codecs=opus";
          console.log(`Using WebM/Opus format with ${options.audioBitsPerSecond} bitrate`);
        } else if (MediaRecorder.isTypeSupported("audio/webm")) {
          mimeType = "audio/webm";
          console.log(`Using WebM format with ${options.audioBitsPerSecond} bitrate`);
        } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4";
          console.log(`Using MP4 format with ${options.audioBitsPerSecond} bitrate`);
        } else if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
          mimeType = "audio/ogg;codecs=opus";
          console.log(`Using OGG/Opus format with ${options.audioBitsPerSecond} bitrate`);
        }

        options.mimeType = mimeType;
        mediaRecorderRef.current = new MediaRecorder(stream, options);
        audioChunksRef.current = [];

        // Set up event handlers before starting
        mediaRecorderRef.current.ondataavailable = async (event) => {
          if (event.data.size > 0) {
            // Claim the index BEFORE the async save so a failed save can't shift/overwrite indexes
            const idx = chunkCounterRef.current;
            chunkCounterRef.current = idx + 1;
            audioChunksRef.current.push(event.data);

            // Calculate actual elapsed time from start (also update when chunks are saved)
            const elapsedSeconds = getElapsedSeconds();
            durationCounterRef.current = elapsedSeconds;
            const newDuration = durationCounterRef.current;
            dispatch(setRecordingDuration(newDuration));
            dispatch(setDuration(newDuration));

            if (idx >= MAX_CHUNKS_PER_SESSION) {
              requestMaxDurationStopRef.current();
              return;
            }

            // Save chunk to IndexedDB using the local recordingId variable
            try {
              await RecordingStateDB.saveRecordingChunk(recordingId, idx, event.data);

              // Only advance durable state.chunks after a successful IDB put
              await RecordingStateDB.saveRecordingState(recordingId, {
                isActive: true,
                phase: "recording",
                startTime: startTime,
                chunks: idx + 1,
                mimeType: mimeType,
              });
            } catch (error) {
              console.error("Error saving chunk:", error);
            }
          }
        };

        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          const audioURL = await blobToAudioSrc(audioBlob);

          // Single session — no two-part playback
          clearContinuationRefs();

          // Dispatch stop recording action to Redux
          dispatch(stopRecordingAction({ audioUrl: audioURL }));

          // Stop the stream tracks
          stream.getTracks().forEach((track) => track.stop());

          // Keep restorable after stop (use the local recordingId — Redux value may be stale here)
          await RecordingStateDB.saveRecordingState(recordingId, {
            isActive: true,
            phase: "ready_to_upload",
            startTime: startTime,
            chunks: chunkCounterRef.current,
            mimeType: mimeType,
          });
        };

        // Handle MediaRecorder errors gracefully
        mediaRecorderRef.current.onerror = (event: Event) => {
          console.error("MediaRecorder error:", event);
          toast.error("שגיאה בהקלטה");

          // Attempt to save partial recording if we have any chunks
          if (audioChunksRef.current.length > 0) {
            const partialBlob = new Blob(audioChunksRef.current, { type: mimeType });
            const partialURL = URL.createObjectURL(partialBlob);
            dispatch(stopRecordingAction({ audioUrl: partialURL }));
            toast.info("הקלטה חלקית נשמרה");
          } else {
            dispatch(resetRecording());
          }
        };

        // Start recording with timed chunks (reduces IndexedDB overhead for long recordings)
        mediaRecorderRef.current.start(CHUNK_TIMESLICE_MS);

        // Save initial recording state asynchronously (non-blocking)
        RecordingStateDB.saveRecordingState(recordingId, {
          isActive: true,
          phase: "recording",
          startTime: startTime,
          chunks: 0,
          mimeType: mimeType,
        }).catch((error) => {
          console.error("Error saving initial recording state:", error);
        });
      } catch (error) {
        // Reset Redux state on error
        dispatch(resetRecording());

        if (error && typeof error === "object" && "code" in error && error.code === 8) {
          toast.error(`מיקרופון לא נמצא או אין הרשאות`);
        } else if (error && typeof error === "object" && "message" in error) {
          toast.error(`שגיאה (${String(error.message)})`);
        } else {
          toast.error(`שגיאה לא ידועה`);
        }
      }
    },
    [dispatch, clearContinuationRefs, getElapsedSeconds, setIsRecordingPaused]
  );

  const handleStartRecording = useCallback(
    async (isContinuation = false) => {
      if (isReadOnly) {
        toast.error("לא ניתן להקליט - תקופת הניסיון הסתיימה. שדרג למינוי בתשלום.");
        navigate("/");
        return;
      }
      if (
        isSubmittingUpload ||
        (pendingTranscription &&
          pendingTranscription.currentStep !== "error" &&
          pendingTranscription.currentStep !== "completed")
      ) {
        toast.error("העלאה בתהליך — נא להמתין לסיום");
        return;
      }
      if (startInFlightRef.current || isRecordingRef.current) return;
      startInFlightRef.current = true;
      dispatch(setViewMode("recording"));
      setIsSubmittingUpload(false);
      try {
        await startActualRecording(isContinuation);
      } finally {
        startInFlightRef.current = false;
      }
    },
    [startActualRecording, dispatch, isReadOnly, navigate, isSubmittingUpload, pendingTranscription]
  );

  const stopRecording = useCallback(async () => {
    silenceMonitorRef.current?.finish();
    const recorder = mediaRecorderRef.current;
    // Check both Redux state AND actual MediaRecorder state for reliability
    if (recorder && recorder.state !== "inactive") {
      console.log("stopping recording, MediaRecorder state:", recorder.state);
      durationCounterRef.current = getElapsedSeconds();
      pauseStartRef.current ??= Date.now();
      recorder.stop();
      setIsRecordingPaused(false);
      setCurrentTime(0);
      // Note: stopRecordingAction will be dispatched in onstop handler
    }
  }, [setIsRecordingPaused, getElapsedSeconds]);

  const requestMaxDurationStop = useCallback(() => {
    if (maxDurationStopRequestedRef.current) return;
    maxDurationStopRequestedRef.current = true;
    setShowMaxDurationDialog(true);
    void stopRecording();
  }, [stopRecording]);
  requestMaxDurationStopRef.current = requestMaxDurationStop;

  useEffect(() => {
    if (!isRecording || isRecordingPaused) return;
    if (recordingDuration < MAX_RECORDING_DURATION_SEC) return;
    requestMaxDurationStop();
  }, [isRecording, isRecordingPaused, recordingDuration, requestMaxDurationStop]);

  // Expose stopRecording to global controls
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
    return () => {
      stopRecordingRef.current = null;
    };
  }, [stopRecording, stopRecordingRef]);

  const reset = useCallback(async () => {
    silenceMonitorRef.current?.finish();
    shouldAutoContinueRef.current = false;
    // If there's an active recording in progress, stop it first
    if (isRecording && mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
        console.log("Active recording stopped during reset");
      } catch (error) {
        console.error("Error stopping active recording during reset:", error);
      }
    }
    console.log("activeRecordingId", activeRecordingId, RecordingStateDB ? "true" : "false");
    // If there's an active recording, delete it from IndexedDB first
    if (activeRecordingId && RecordingStateDB) {
      try {
        console.log("Attempting to delete active recording from IndexedDB");
        await RecordingStateDB.deleteRecordingById(activeRecordingId);
        console.log("Active recording deleted during reset:", activeRecordingId);
      } catch (error) {
        console.error("Error deleting active recording during reset:", error);
      }
    }

    // Reset Redux state
    dispatch(resetRecording());
    setShowMaxDurationDialog(false);
    setCurrentTime(0);
    setLocalFileName(null);
    clearContinuationRefs();

    // Clear audio chunks
    audioChunksRef.current = [];
    chunkCounterRef.current = 0;
    durationCounterRef.current = 0;
    recordingStartTimeRef.current = 0;
    hasShownUploadSuccessRef.current = false;
    lastProcessedStatusRef.current = null;

    // Stop and cleanup media stream if it exists
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Clear media recorder reference
    mediaRecorderRef.current = null;

    console.log("Reset completed - all recording state cleared");

    // Show success message
    toast.success("הקלטה נמחקה בהצלחה");
  }, [isRecording, activeRecordingId, dispatch, clearContinuationRefs]);

  // Continue recording function
  const handleContinueRecording = useCallback(async () => {
    if (!audioSrc || isSubmittingUpload) return;

    try {
      // Use existing active recording ID if available, otherwise create new one
      let recordingId = activeRecordingId;
      if (!recordingId) {
        const timestamp = Date.now();
        recordingId = `recording_${timestamp}`;
        dispatch(setActiveRecordingId(recordingId));
      }

      // Get existing chunks and recording state from IndexedDB
      let existingChunks: Array<{ buffer?: ArrayBuffer; data?: Blob; chunkIndex: number }> = [];
      let recordingState: { mimeType?: string } | null = null;
      try {
        existingChunks = await RecordingStateDB.getRecordingChunks(recordingId);
        const state = await RecordingStateDB.getRecordingState(recordingId);
        recordingState = state ? { mimeType: state.mimeType } : null;
        console.log("Existing chunks found:", existingChunks.length);
      } catch (error) {
        console.error("Error getting existing chunks:", error);
      }

      const restoredDurationSec =
        existingChunks.filter((chunk) => chunk.buffer || chunk.data).length * CHUNK_INTERVAL_SEC;
      if (restoredDurationSec >= MAX_RECORDING_DURATION_SEC) {
        setShowMaxDurationDialog(true);
        return;
      }
      maxDurationStopRequestedRef.current = false;

      const existingMimeType = recordingState?.mimeType || "audio/webm;codecs=opus";
      // Store existing chunks in audioChunksRef (new chunks store ArrayBuffer, legacy — Blob)
      audioChunksRef.current = existingChunks
        .map((chunk) =>
          chunk.buffer ? new Blob([chunk.buffer], { type: existingMimeType }) : chunk.data
        )
        .filter((blob): blob is Blob => !!blob);

      const existingChunksCount = audioChunksRef.current.length;
      // Wall-clock duration of the already-recorded part (chunks arrive every CHUNK_INTERVAL_SEC)
      const existingDurationSec = existingChunksCount * CHUNK_INTERVAL_SEC;

      const startTime = Date.now();
      chunkCounterRef.current = existingChunksCount;
      continuationFirstNewChunkFullRef.current = null;
      durationCounterRef.current = existingDurationSec;
      pausedDurationRef.current = 0;
      pauseStartRef.current = null;
      setIsRecordingPaused(false);
      recordingStartTimeRef.current = Date.now() - existingDurationSec * 1000;

      dispatch(setViewMode("recording"));
      dispatch(startRecordingAction({ recordingId }));

      // Start new recording session
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Note: sampleRate constraint is ignored by browsers - they use hardware rate
          // Backend will resample to 16kHz for Whisper
          channelCount: 1, // Mono audio for speech
        },
      });
      streamRef.current = stream;

      // Set up audio analyzer for visualization
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;
      audioContextRef.current = audioContext;

      // Mark analyser as ready for visualization
      dispatch(setIsAnalyserReady(true));

      // Determine best supported audio format
      // Use stored mimeType from previous recording session, or detect best format
      let mimeType = recordingState?.mimeType;
      const options: MediaRecorderOptions = {
        audioBitsPerSecond: 64000, // 64kbps - good quality for speech, ~28MB per hour
      };

      if (!mimeType) {
        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
          mimeType = "audio/webm;codecs=opus";
          console.log(`Using WebM/Opus format with ${options.audioBitsPerSecond} bitrate`);
        } else if (MediaRecorder.isTypeSupported("audio/webm")) {
          mimeType = "audio/webm";
          console.log(`Using WebM format with ${options.audioBitsPerSecond} bitrate`);
        } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
          mimeType = "audio/mp4";
          console.log(`Using MP4 format with ${options.audioBitsPerSecond} bitrate`);
        } else if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
          mimeType = "audio/ogg;codecs=opus";
          console.log(`Using OGG/Opus format with ${options.audioBitsPerSecond} bitrate`);
        }
      }

      options.mimeType = mimeType;
      mediaRecorderRef.current = new MediaRecorder(stream, options);

      // Update recording state to mark as active
      await RecordingStateDB.saveRecordingState(recordingId, {
        isActive: true,
        phase: "recording",
        startTime: startTime,
        chunks: existingChunksCount,
        mimeType: mimeType,
      });

      // Set up data available handler for new chunks
      mediaRecorderRef.current.ondataavailable = async (event) => {
        if (event.data.size === 0) return;

        // The first chunk of a new MediaRecorder session carries a WebM init segment.
        // For playback we strip it (part2 blob gets the FULL chunk instead), but the
        // full chunk goes to IndexedDB so restore/upload keeps a decodable stream.
        const isFirstNewChunk = audioChunksRef.current.length === existingChunksCount;
        let chunkToAppend: Blob = event.data;
        const chunkToSave: Blob = event.data;

        if (isFirstNewChunk) {
          continuationFirstNewChunkFullRef.current = event.data;
          const { blob, hasCluster } = await stripWebMInitSegment(event.data);
          if (!hasCluster) {
            // Init-only chunk (no media data) — don't push to the playback buffer
            return;
          }
          chunkToAppend = blob;
        }

        // Claim the index BEFORE the async save so a failed save can't shift/overwrite indexes
        const idx = chunkCounterRef.current;
        chunkCounterRef.current = idx + 1;
        audioChunksRef.current.push(chunkToAppend);

        // Calculate actual elapsed time from adjusted start (also update when chunks are saved)
        const elapsedSeconds = getElapsedSeconds();
        durationCounterRef.current = elapsedSeconds;
        const newDuration = durationCounterRef.current;
        dispatch(setRecordingDuration(newDuration));
        dispatch(setDuration(newDuration));

        if (idx >= MAX_CHUNKS_PER_SESSION) {
          requestMaxDurationStopRef.current();
          return;
        }

        // Save chunk to IndexedDB
        try {
          await RecordingStateDB.saveRecordingChunk(recordingId!, idx, chunkToSave);

          // Only advance durable state.chunks after a successful IDB put
          await RecordingStateDB.saveRecordingState(recordingId!, {
            isActive: true,
            phase: "recording",
            startTime: startTime,
            chunks: idx + 1,
            mimeType: mimeType,
          });
        } catch (error) {
          console.error("Error saving chunk in continue recording:", error);
        }
      };

      // Set up stop handler
      mediaRecorderRef.current.onstop = async () => {
        // Stop the stream tracks
        stream.getTracks().forEach((track) => track.stop());

        // Keep restorable after stop
        RecordingStateDB.saveRecordingState(recordingId!, {
          isActive: true,
          phase: "ready_to_upload",
          startTime: startTime,
          chunks: chunkCounterRef.current,
          mimeType: mimeType,
        }).catch((error) => {
          console.error("Error saving state on stop (continue):", error);
        });

        const chunksForBlob = audioChunksRef.current;
        // Preserve active elapsed duration, excluding explicit pauses.
        const totalDurationSec = getElapsedSeconds();

        try {
          if (existingChunksCount > 0) {
            // Two-part playback: merged WebM is not playable/seekable across the session
            // boundary. Play part1 (old session) then part2 (new session) in sequence.
            // Part2 must include the init segment (full first new chunk) or the browser
            // can't decode it (NotSupportedError).
            const boundary = existingChunksCount;
            const part1Blob = new Blob(chunksForBlob.slice(0, boundary), { type: mimeType });
            const firstNewChunkFull = continuationFirstNewChunkFullRef.current;
            const part2Chunks =
              firstNewChunkFull != null
                ? [firstNewChunkFull, ...chunksForBlob.slice(boundary + 1)]
                : chunksForBlob.slice(boundary);
            const part2Blob = new Blob(part2Chunks, { type: mimeType });
            continuationFirstNewChunkFullRef.current = null;

            const [part1URL, part2URL] = await Promise.all([
              blobToAudioSrc(part1Blob),
              blobToAudioSrc(part2Blob),
            ]);
            continuationPart1DurationRef.current = boundary * CHUNK_INTERVAL_SEC;
            continuationPart2SrcRef.current = part2URL;
            continuationPart1SrcRef.current = part1URL;
            nowPlayingPart2Ref.current = false;

            dispatch(stopRecordingAction({ audioUrl: part1URL }));
          } else {
            // No previous session in the buffer — single blob playback
            const combinedBlob = new Blob(chunksForBlob, { type: mimeType });
            const audioURL = await blobToAudioSrc(combinedBlob);
            clearContinuationRefs();
            dispatch(stopRecordingAction({ audioUrl: audioURL }));
          }

          dispatch(setRecordingDuration(totalDurationSec));
          dispatch(setDuration(totalDurationSec));
        } catch (error) {
          console.error("Error creating audio URL after continue stop:", error);
          toast.error("שגיאה בהקלטה");
        }
      };

      // Handle MediaRecorder errors gracefully
      mediaRecorderRef.current.onerror = (event: Event) => {
        console.error("MediaRecorder error (continue):", event);
        toast.error("שגיאה בהקלטה");

        // Attempt to save partial recording if we have any chunks
        if (audioChunksRef.current.length > 0) {
          const partialBlob = new Blob(audioChunksRef.current, { type: mimeType });
          const partialURL = URL.createObjectURL(partialBlob);
          dispatch(stopRecordingAction({ audioUrl: partialURL }));
          toast.info("הקלטה חלקית נשמרה");
        } else {
          dispatch(resetRecording());
        }
      };

      // Start recording with timed chunks
      mediaRecorderRef.current.start(CHUNK_TIMESLICE_MS);
    } catch (error) {
      console.error("Error continuing recording:", error);
      if (error && typeof error === "object" && "code" in error && error.code === 8) {
        toast.error(`מיקרופון לא נמצא או אין הרשאות`);
      } else if (error && typeof error === "object" && "message" in error) {
        toast.error(`שגיאה (${String(error.message)})`);
      } else {
        toast.error(`שגיאה לא ידועה`);
      }
    }
  }, [audioSrc, activeRecordingId, dispatch, clearContinuationRefs, isSubmittingUpload, getElapsedSeconds, setIsRecordingPaused]);

  // Screen lock / OS interruption can end or mute the mic track while MediaRecorder keeps
  // running and records silence. Close the dead session and resume in a fresh one.
  useEffect(() => {
    const stream = streamRef.current;
    const track = stream?.getAudioTracks()[0];
    if (!isRecording || !isAnalyserReady || !stream || !track) return;
    let checkTimer: number | undefined;

    const resumeInNewSession = () => {
      const recorder = mediaRecorderRef.current;
      if (streamRef.current !== stream || !recorder) return;
      shouldAutoContinueRef.current = true;
      // A recorder whose track ended stops on its own; the auto-continue effect picks it up
      if (recorder.state !== "inactive") void stopRecording();
    };
    const handleTrackEnded = () => {
      shouldAutoContinueRef.current = true;
      if (document.visibilityState === "visible") resumeInNewSession();
    };
    const checkTrack = () => {
      if (document.visibilityState !== "visible") return;
      const isDead = track.readyState === "ended";
      const isMuted = track.muted && mediaRecorderRef.current?.state === "recording";
      if (isDead || isMuted) resumeInNewSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (audioContextRef.current?.state === "suspended") {
        void audioContextRef.current.resume().catch(() => {});
      }
      window.clearTimeout(checkTimer);
      // iOS unmutes the track shortly after unlock — only recover if it stays muted
      checkTimer = window.setTimeout(checkTrack, 1500);
    };

    track.addEventListener("ended", handleTrackEnded);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.clearTimeout(checkTimer);
      track.removeEventListener("ended", handleTrackEnded);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isRecording, isAnalyserReady, stopRecording]);

  useEffect(() => {
    if (isRecording || viewMode !== "after-record" || !audioSrc || !shouldAutoContinueRef.current) return;
    const tryAutoContinue = () => {
      // getUserMedia needs a visible page
      if (!shouldAutoContinueRef.current || document.visibilityState !== "visible") return;
      shouldAutoContinueRef.current = false;
      Sentry.addBreadcrumb({ category: "recording.interruption", message: "auto-continue", level: "info",
        data: { recordingId: activeRecordingId } });
      void handleContinueRecording();
    };
    tryAutoContinue();
    document.addEventListener("visibilitychange", tryAutoContinue);
    return () => document.removeEventListener("visibilitychange", tryAutoContinue);
  }, [isRecording, viewMode, audioSrc, activeRecordingId, handleContinueRecording]);

  useEffect(() => {
    continueRecordingRef.current = () => {
      void handleContinueRecording();
    };
    return () => {
      continueRecordingRef.current = null;
    };
  }, [continueRecordingRef, handleContinueRecording]);

  const handleReset = useCallback(() => {
    setShowResetDialog(true);
  }, []);

  const confirmReset = useCallback(async () => {
    try {
      // If there's an active recording, discard it first
      if (hasActiveRecording && activeRecordingId) {
        await discardActiveRecording();
      }
      await reset();
      setShowResetDialog(false);
    } catch (error) {
      console.error("Error during reset:", error);
      toast.error("שגיאה במחיקת ההקלטה");
    }
  }, [hasActiveRecording, activeRecordingId, discardActiveRecording, reset]);

  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);

  // Audio playback functions
  const togglePlay = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.error("Error playing audio:", error);
          });
      }
    }
  }, [isPlaying]);

  // Stop playback and return to beginning (part1 in two-part mode)
  const stopPlayback = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    setIsPlaying(false);
    // In two-part mode, switch back to part1 so next play starts from the very beginning
    if (nowPlayingPart2Ref.current && continuationPart1SrcRef.current) {
      nowPlayingPart2Ref.current = false;
      el.src = continuationPart1SrcRef.current;
    }
    el.currentTime = 0;
    setCurrentTime(0);
  }, []);

  /** Switch <audio> src and seek once the new source can play (avoids seeking before metadata). */
  const switchPlaybackSource = useCallback(
    (source: string, targetTime: number, shouldPlay: boolean, _targetPart: "part1" | "part2") => {
      const el = audioRef.current;
      if (!el) return;

      if (pendingCanPlayHandlerRef.current) {
        el.removeEventListener("canplay", pendingCanPlayHandlerRef.current);
        pendingCanPlayHandlerRef.current = null;
      }

      const requestId = sourceSwitchRequestRef.current + 1;
      sourceSwitchRequestRef.current = requestId;
      const isCurrentlyPlaying = shouldPlay && !el.paused;

      const applySeek = () => {
        if (requestId !== sourceSwitchRequestRef.current) return;
        const maxSeekTime =
          Number.isFinite(el.duration) && el.duration > 0
            ? Math.max(0, el.duration - 0.05)
            : targetTime;
        const safeTargetTime = Math.min(Math.max(0, targetTime), maxSeekTime);
        setAudioCurrentTime(el, safeTargetTime);
        pendingCanPlayHandlerRef.current = null;
        if (!isCurrentlyPlaying) return;
        el.play().catch((error) => {
          console.warn("Playback resume after source switch failed:", error);
        });
      };

      const currentSource = el.currentSrc || el.src;
      if (currentSource === source) {
        applySeek();
        return;
      }

      const handleCanPlay = () => applySeek();
      pendingCanPlayHandlerRef.current = handleCanPlay;
      el.addEventListener("canplay", handleCanPlay, { once: true });
      el.pause();
      el.src = source;
      el.load();
    },
    []
  );

  /** Seek to an absolute time position, handling two-part playback across the session boundary. */
  const seekToTime = useCallback(
    (targetTime: number) => {
      const el = audioRef.current;
      if (!el) return;

      const part1Duration = continuationPart1DurationRef.current;

      if (part1Duration > 0) {
        if (targetTime < part1Duration) {
          // Target is inside part1 — switch back if needed
          if (nowPlayingPart2Ref.current && continuationPart1SrcRef.current) {
            nowPlayingPart2Ref.current = false;
            switchPlaybackSource(continuationPart1SrcRef.current, targetTime, isPlaying, "part1");
          } else {
            setAudioCurrentTime(el, targetTime);
          }
          setCurrentTime(targetTime);
        } else {
          // Target is inside part2 — switch forward if needed
          const part2Time = targetTime - part1Duration;
          if (!nowPlayingPart2Ref.current && continuationPart2SrcRef.current) {
            nowPlayingPart2Ref.current = true;
            switchPlaybackSource(continuationPart2SrcRef.current, part2Time, isPlaying, "part2");
          } else {
            setAudioCurrentTime(el, part2Time);
          }
          setCurrentTime(targetTime);
        }
      } else {
        setAudioCurrentTime(el, Math.max(0, Math.min(duration, targetTime)));
        setCurrentTime(el.currentTime);
      }
    },
    [duration, isPlaying, switchPlaybackSource]
  );

  /** Current playback position in absolute (total-recording) time. */
  const getAbsoluteCurrentTime = useCallback(() => {
    const el = audioRef.current;
    if (!el) return 0;
    const part1Duration = continuationPart1DurationRef.current;
    return part1Duration > 0 && nowPlayingPart2Ref.current
      ? part1Duration + el.currentTime
      : el.currentTime;
  }, []);

  const seekBackward = useCallback(() => {
    if (!audioRef.current) return;
    seekToTime(Math.max(0, getAbsoluteCurrentTime() - 10));
  }, [seekToTime, getAbsoluteCurrentTime]);

  const seekForward = useCallback(() => {
    if (!audioRef.current || duration <= 0) return;
    seekToTime(Math.min(duration, getAbsoluteCurrentTime() + 10));
  }, [seekToTime, getAbsoluteCurrentTime, duration]);

  const handleSeek = useCallback(
    (value: number[]) => {
      if (!audioRef.current || duration <= 0) return;
      const newTime = (value[0] / 100) * duration;
      seekToTime(Math.max(0, Math.min(duration, newTime)));
    },
    [duration, seekToTime]
  );

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    if (nowPlayingPart2Ref.current && continuationPart1DurationRef.current > 0) {
      setCurrentTime(continuationPart1DurationRef.current + audioRef.current.currentTime);
    } else {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    // In two-part mode, total duration was already set by the onstop/restore handler — don't overwrite it
    if (continuationPart1DurationRef.current > 0) return;
    const reported = audioRef.current?.duration ?? NaN;
    if (Number.isFinite(reported) && reported > 0) {
      dispatch(setDuration(reported));
    }
  }, [dispatch]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleEnded = useCallback(() => {
    const part2Src = continuationPart2SrcRef.current;
    const alreadyOnPart2 = nowPlayingPart2Ref.current;
    const el = audioRef.current;
    const part1ActuallyPlayed = el != null && el.readyState >= 2 && el.currentTime > 0.5;

    // Part1 finished — chain part2 seamlessly
    if (part2Src && el && !alreadyOnPart2 && part1ActuallyPlayed) {
      nowPlayingPart2Ref.current = true;
      el.src = part2Src;
      el.play().catch((error) => {
        console.error("Audio part2 play failed:", error);
        setIsPlaying(false);
      });
      return;
    }
    nowPlayingPart2Ref.current = false;
    setIsPlaying(false);
    setCurrentTime(duration);
  }, [duration]);

  // Reset audio state when view mode changes
  useEffect(() => {
    if (viewMode !== "after-record") {
      setIsPlaying(false);
      setCurrentTime(0);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [viewMode]);

  const handleToggleRecordingPause = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || typeof recorder.pause !== 'function' || typeof recorder.resume !== 'function') return;
    if (recorder.state === 'recording') {
      recorder.pause();
      silenceMonitorRef.current?.reset();
      pauseStartRef.current = Date.now();
      setIsRecordingPaused(true);
    } else if (recorder.state === 'paused') {
      recorder.resume();
      pausedDurationRef.current += Date.now() - (pauseStartRef.current ?? Date.now());
      pauseStartRef.current = null;
      setIsRecordingPaused(false);
    }
  }, [setIsRecordingPaused]);
  const canPauseRecording = IS_RECORDING_PAUSE_ENABLED && isRecording && typeof mediaRecorderRef.current?.pause === 'function' &&
    typeof mediaRecorderRef.current?.resume === 'function';
  useEffect(() => {
    pauseRecordingRef.current = canPauseRecording ? handleToggleRecordingPause : null;
    return () => { pauseRecordingRef.current = null; };
  }, [canPauseRecording, handleToggleRecordingPause, pauseRecordingRef]);

  const silenceMonitor = useRecordingSilenceWarning({
    enabled: isRecording && !isRecordingPaused,
    sessionId: activeRecordingId,
    storageScope: silenceStorageScope,
    readAudio: () => ({ analyser: analyserRef.current, context: audioContextRef.current,
      track: streamRef.current?.getAudioTracks()[0], recorder: mediaRecorderRef.current }),
    onWarning: () => { toast.warning(t('recordingSilence.message'), { id: 'recording-silence' }); },
    onClear: () => { toast.dismiss('recording-silence'); },
    onEvent: (event, data) => { Sentry.addBreadcrumb({ category: 'recording.silence', message: event,
      level: 'info', data: { recordingId: activeRecordingId, ...data } }); },
  });
  silenceMonitorRef.current = silenceMonitor;
  useEffect(() => {
    dismissSilenceWarningRef.current = silenceMonitor.dismiss;
    return () => { dismissSilenceWarningRef.current = null; };
  }, [dismissSilenceWarningRef, silenceMonitor.dismiss]);
  const { isWarningVisible: isSilenceWarningVisible } = silenceMonitor;
  useEffect(() => {
    setIsSilenceWarningVisible(isSilenceWarningVisible);
  }, [isSilenceWarningVisible, setIsSilenceWarningVisible]);
  useEffect(() => () => {
    setIsSilenceWarningVisible(false);
    setIsRecordingPaused(false);
  }, [setIsSilenceWarningVisible, setIsRecordingPaused]);
  useEffect(() => {
    if (!isRecording) setIsRecordingPaused(false);
  }, [isRecording, setIsRecordingPaused]);

  // Main render logic
  const isPipelineBusy =
    isSubmittingUpload ||
    (!!pendingTranscription &&
      pendingTranscription.currentStep !== "error" &&
      pendingTranscription.currentStep !== "completed");

  return (
    <div>
      <RecordingSilenceAlert visible={isSilenceWarningVisible}
        onPause={canPauseRecording ? handleToggleRecordingPause : undefined} onStop={stopRecording}
        onDismiss={silenceMonitor.dismiss} />
      {viewMode === "main" && (
        <MainInterface
          onStartRecording={handleStartRecording}
          onGoogleDriveUpload={handleGoogleDriveUpload}
          onFileDrop={handleFileDrop}
          isReadOnly={isReadOnly}
          isArchiveMode={isArchiveMode}
          isBusy={isPipelineBusy}
        />
      )}
      {viewMode === "recording" && (
        <RecordingInterface recordingDuration={recordingDuration} canvasRef={canvasRef} onStopRecording={stopRecording}
          isPaused={isRecordingPaused} isSilenceWarningVisible={isSilenceWarningVisible} onTogglePause={canPauseRecording ? handleToggleRecordingPause : undefined} />
      )}
      {viewMode === "after-record" && (
        <AfterRecordingInterface
          audioSrc={audioSrc}
          audioRef={audioRef}
          isPlaying={isPlaying}
          duration={duration}
          currentTime={currentTime}
          isUploading={isSubmittingUpload}
          fileName={localFileName}
          activeRecordingId={activeRecordingId}
          onUpload={uploadAudio}
          onContinueRecording={handleContinueRecording}
          onReset={handleReset}
          onTogglePlay={togglePlay}
          onStopPlayback={stopPlayback}
          onSeekBackward={seekBackward}
          onSeekForward={seekForward}
          onSeek={handleSeek}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
        />
      )}

      {/* Google Drive Picker */}
      <GoogleDrivePicker
        isOpen={showGoogleDrivePicker}
        onFileSelect={handleGoogleDriveFileSelect}
        onClose={handleGoogleDrivePickerClose}
      />

      <AlertDialog open={showFileTooLargeDialog} onOpenChange={setShowFileTooLargeDialog}>
        <AlertDialogContent
          dir={language === "he" ? "rtl" : "ltr"}
          className="border-destructive bg-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              {t("fileTooLarge.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-destructive">
              {t("fileTooLarge.message")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("fileTooLarge.close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showMaxDurationDialog} onOpenChange={setShowMaxDurationDialog}>
        <AlertDialogContent
          dir={language === "he" ? "rtl" : "ltr"}
          className="top-6 translate-y-0 border-destructive bg-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              {t("recordingMaxDuration.title")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-destructive">
              {t("recordingMaxDuration.message").replace(
                "{minutes}",
                String(Math.floor(MAX_RECORDING_DURATION_SEC / 60))
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("recordingMaxDuration.close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>מחיקת הקלטה</AlertDialogTitle>
            <AlertDialogDescription>האם אתה בטוח שברצונך למחוק את ההקלטה? פעולה זו לא ניתנת לביטול.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ביטול</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              מחק הקלטה
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
